import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import apiService from '../../services/apiService';

// #512: Zustand des KayCee-Assistenten, EINMAL für die ganze Anwendung.
//
// Er lebt oberhalb des Routers (App.js), nicht in der Sprechblase: Eine
// laufende Antwort und eine offene Blase überstehen so jeden Ansichtswechsel
// (Lehre aus #312, die Lebensdauer des Zustands gehört an den richtigen Ort).
// Die Mobilansicht benutzt denselben Zustand und damit denselben Verlauf.
//
// Den Verlauf hält der Server (#513). Hier liegt nur, was gerade angezeigt
// wird. Beim Öffnen der Blase wird er neu geladen, damit ein zweiter Tab oder
// ein anderes Gerät denselben Stand zeigt und nach dem nächtlichen Leeren
// nichts Altes stehen bleibt.

const AssistentKontext = createContext(null);

const STAND_LEER = { promptTokens: null, grenzen: null, hinweis: false };

function standAus(antwort) {
  return { promptTokens: antwort?.promptTokens ?? null, grenzen: antwort?.grenzen || null, hinweis: antwort?.hinweis === true };
}

// Fehlerart aus der Antwort des Servers; die Texte übersetzt die Oberfläche.
export function fehlerArt(err) {
  const daten = err?.response?.data || {};
  if (daten.aiDisabled || daten.code === 'aiDisabled') return 'abgeschaltet';
  if (daten.code === 'aiNotConfigured') return 'nichtEingerichtet';
  if (daten.code === 'laeuftNoch') return 'laeuftNoch';
  if (daten.code === 'werkzeugeNichtUnterstuetzt') return 'keineWerkzeuge';
  if (!err?.response) return 'netz';
  return 'allgemein';
}

export function AssistentProvider({ children }) {
  const [offen, setOffen] = useState(false);
  const [anzeige, setAnzeige] = useState([]);
  const [stand, setStand] = useState(STAND_LEER);
  const [geladen, setGeladen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const laeuftRef = useRef(false);

  const laden = useCallback(async () => {
    if (laeuftRef.current) return; // die laufende Runde bringt ihren Stand selbst mit
    try {
      const antwort = await apiService.assistentStand();
      if (laeuftRef.current) return;
      setAnzeige(Array.isArray(antwort?.anzeige) ? antwort.anzeige : []);
      setStand(standAus(antwort));
      setGeladen(true);
    } catch (err) {
      setFehler(fehlerArt(err));
    }
  }, []);

  // Eine Aktion auf dem Verlauf; während sie läuft, ist alles andere gesperrt
  // (der Server antwortet sonst ohnehin 409).
  const aktion = useCallback(async (ausfuehren) => {
    if (laeuftRef.current) return false;
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      await ausfuehren();
      return true;
    } catch (err) {
      setFehler(fehlerArt(err));
      return false;
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  }, []);

  const senden = useCallback((text) => {
    const frage = String(text || '').trim();
    if (!frage) return Promise.resolve(false);
    const vorher = { role: 'user', content: frage, ts: new Date().toISOString(), wartet: true };
    return aktion(async () => {
      setAnzeige((a) => [...a, vorher]);
      try {
        const antwort = await apiService.assistentNachricht(frage);
        if (antwort?.zusammengefasst) {
          // Der Server hat vor der Runde zusammengefasst und das vermerkt: den
          // Stand von dort holen, damit der Vermerk an der richtigen Stelle steht.
          const neu = await apiService.assistentStand();
          setAnzeige(Array.isArray(neu?.anzeige) ? neu.anzeige : []);
        } else {
          setAnzeige((a) => [...a.filter((e) => e !== vorher), { ...vorher, wartet: undefined }, antwort.eintrag]);
        }
        setStand(standAus(antwort));
      } catch (err) {
        // Der Server hat nichts gespeichert: die Frage wieder herausnehmen
        setAnzeige((a) => a.filter((e) => e !== vorher));
        throw err;
      }
    });
  }, [aktion]);

  const zusammenfassen = useCallback(() => aktion(async () => {
    const antwort = await apiService.assistentZusammenfassen();
    setAnzeige(Array.isArray(antwort?.anzeige) ? antwort.anzeige : []);
    setStand(standAus(antwort));
  }), [aktion]);

  const leeren = useCallback(() => aktion(async () => {
    await apiService.assistentLeeren();
    setAnzeige([]);
    setStand((s) => ({ ...s, promptTokens: null, hinweis: false }));
  }), [aktion]);

  const speichern = useCallback(() => aktion(async () => {
    await apiService.assistentSpeichern();
  }), [aktion]);

  const oeffneGespeichert = useCallback((id) => aktion(async () => {
    const antwort = await apiService.assistentOeffnen(id);
    setAnzeige(Array.isArray(antwort?.anzeige) ? antwort.anzeige : []);
    setStand(standAus(antwort));
  }), [aktion]);

  const wert = useMemo(() => ({
    offen, setOffen, anzeige, stand, geladen, laeuft, fehler, setFehler,
    laden, senden, zusammenfassen, leeren, speichern, oeffneGespeichert
  }), [offen, anzeige, stand, geladen, laeuft, fehler, laden, senden, zusammenfassen, leeren, speichern, oeffneGespeichert]);

  return <AssistentKontext.Provider value={wert}>{children}</AssistentKontext.Provider>;
}

export function useAssistent() {
  const kontext = useContext(AssistentKontext);
  if (!kontext) throw new Error('useAssistent braucht den AssistentProvider (App.js)');
  return kontext;
}
